
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mic, Play, Volume2, Lock } from 'lucide-react';
import { usePodcastGenerator } from '@/hooks/usePodcastGenerator';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import GoogleMapSearchBar, { LocationDetails } from '@/components/GoogleMapSearchBar';

interface PodcastGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onPodcastGenerated?: (podcast: any) => void;
  initialSearchText?: string;
  preselectedLanguage?: 'fr' | 'en' | null;
}

const PodcastGenerator: React.FC<PodcastGeneratorProps> = ({
  isOpen,
  onClose,
  onPodcastGenerated,
  initialSearchText = '',
  preselectedLanguage = null
}) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationDetails | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'fr' | 'en'>('fr');
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { t, language } = useTranslation();
  const {
    isGenerating,
    generatedPodcast,
    error,
    availableLanguages,
    isLocationBlocked,
    generatePodcast,
    checkExistingPodcasts,
    reset
  } = usePodcastGenerator();

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [forceCenter, setForceCenter] = useState(false);

  const handleLocationSelect = async (location: LocationDetails) => {
    console.log('🎯 Location selected in PodcastGenerator:', location);
    setSelectedLocation(location);
    
    // Check for existing podcasts at this location
    await checkExistingPodcasts(location);
    
    // Reset language selection if current selection is not available
    if (!availableLanguages.includes(selectedLanguage) && availableLanguages.length > 0) {
      setSelectedLanguage(availableLanguages[0]);
    }
  };

  // Effect to handle preselected language
  useEffect(() => {
    if (preselectedLanguage && availableLanguages.includes(preselectedLanguage)) {
      setSelectedLanguage(preselectedLanguage);
    }
  }, [preselectedLanguage, availableLanguages]);

  // Suivi de l'ouverture du clavier virtuel et recentrage dynamique
  useEffect(() => {
    if (!isOpen) return;
    const vv = (window as any).visualViewport as any;
    const onResize = () => {
      if (!vv) return;
      const ratio = vv.height / window.innerHeight;
      setIsKeyboardOpen(ratio < 0.85);
    };
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      onResize();
    }
    return () => {
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, [isOpen]);

  // Recentrer après la création du podcast (le clavier n'est plus nécessaire)
  useEffect(() => {
    if (generatedPodcast) {
      setForceCenter(true);
      try { (document.activeElement as HTMLElement | null)?.blur(); } catch {}
      setIsKeyboardOpen(false);
    } else {
      setForceCenter(false);
    }
  }, [generatedPodcast]);

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      toast({
        title: t('authRequired'),
        description: t('authRequiredDesc'),
        variant: "destructive",
      });
      onClose();
      window.location.href = '/auth';
      return;
    }

    if (!selectedLocation || isLocationBlocked) {
      console.warn('❌ Cannot generate podcast - location blocked or not selected');
      return;
    }

    try {
      console.log('🎙️ Starting podcast generation for location:', {
        name: selectedLocation.name,
        city: selectedLocation.city,
        country: selectedLocation.country,
        coordinates: [selectedLocation.latitude, selectedLocation.longitude],
        language: selectedLanguage
      });
      
      const podcast = await generatePodcast(selectedLocation, selectedLanguage);
      console.log('✅ Podcast generated successfully:', podcast);
      
      if (onPodcastGenerated) {
        onPodcastGenerated(podcast);
      }
    } catch (error) {
      console.error('❌ Error generating podcast:', error);
    }
  };

  const handleClose = () => {
    console.log('🚪 Closing PodcastGenerator dialog');
    reset();
    setSelectedLocation(null);
    setSelectedLanguage('fr');
    onClose();
  };

  const currentDescription = generatedPodcast ? selectedLanguage === 'fr' ? generatedPodcast.Description_FR : generatedPodcast.Description_EN : '';
  const currentAudioFile = generatedPodcast ? selectedLanguage === 'fr' ? generatedPodcast.AudioFile_FR : generatedPodcast.AudioFile_EN : null;

  const isLanguageDisabled = (lang: 'fr' | 'en') => {
    return !availableLanguages.includes(lang);
  };

  const positionClass = (forceCenter || !isKeyboardOpen)
    ? 'top-1/2 translate-y-[-50%] sm:top-1/2 sm:translate-y-[-50%]'
    : 'top-[40%] translate-y-[-40%] sm:top-1/2 sm:translate-y-[-50%]';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-md max-h-[85dvh] overflow-y-auto p-4 rounded-xl ${positionClass}">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mic className="w-5 h-5 text-primary" />
            {t('createPodcastTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('choosePlace')}
          </p>

          {/* Google Maps Search Bar with integrated map */}
          <GoogleMapSearchBar 
            onLocationSelect={handleLocationSelect} 
            selectedLocation={selectedLocation} 
            placeholder={t('generatorSearchPlaceholder')} 
            initialValue={initialSearchText} 
          />

          {/* Location Status */}
          {selectedLocation && isLocationBlocked && (
            <Card className="p-3 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  {t('placeComplete')}
                </span>
              </div>
              <p className="text-xs mt-1 text-orange-700 dark:text-orange-300">
                {t('placeCompleteDesc')}
              </p>
            </Card>
          )}

          {selectedLocation && !isLocationBlocked && availableLanguages.length < 2 && (
            <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t('limitedLanguages')}
                </span>
              </div>
              <p className="text-xs mt-1 text-blue-700 dark:text-blue-300">
                {t('limitedLanguagesDesc')}
              </p>
            </Card>
          )}

          {/* Language Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('podcastLanguage')}</Label>
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              <Button 
                variant={selectedLanguage === 'fr' ? 'default' : 'ghost'} 
                onClick={() => setSelectedLanguage('fr')} 
                className="flex-1 h-8 text-xs rounded-md" 
                size="sm"
                disabled={isLanguageDisabled('fr')}
              >
                🇫🇷 Français
                {isLanguageDisabled('fr') && <Lock className="w-3 h-3 ml-1" />}
              </Button>
              <Button 
                variant={selectedLanguage === 'en' ? 'default' : 'ghost'} 
                onClick={() => setSelectedLanguage('en')} 
                className="flex-1 h-8 text-xs rounded-md" 
                size="sm"
                disabled={isLanguageDisabled('en')}
              >
                🇬🇧 English
                {isLanguageDisabled('en') && <Lock className="w-3 h-3 ml-1" />}
              </Button>
            </div>
          </div>

          {/* Generate Button */}
          <Button 
            onClick={handleGenerate} 
            disabled={!selectedLocation || isGenerating || isLocationBlocked} 
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl h-12 disabled:opacity-50 disabled:cursor-not-allowed" 
            size="lg"
          >
            {isLocationBlocked ? (
              <>
                <Lock className="w-5 h-5 mr-2" />
                {t('placeComplete')}
              </>
            ) : isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('generating')}
              </>
            ) : (
              <>
                <Mic className="w-5 h-5 mr-2" />
                {t('generatePodcast')}
              </>
            )}
          </Button>

          {/* Loading State */}
          {isGenerating && (
            <Card className="p-6 text-center bg-primary/5 border-primary/20">
              <h4 className="font-medium mb-2">{t('podcastInProgress')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('podcastProgressDesc')}
              </p>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card className="p-4 bg-destructive/5 border-destructive/20">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-destructive rounded-full" />
                <span className="font-medium text-destructive">{t('errorTitle')}</span>
              </div>
              <p className="text-sm mt-2 text-destructive">{error}</p>
              <Button 
                onClick={() => selectedLocation && handleGenerate()} 
                variant="outline" 
                size="sm" 
                className="mt-3" 
                disabled={!selectedLocation || isLocationBlocked}
              >
                {t('retry')}
              </Button>
            </Card>
          )}

          {/* Success State */}
          {generatedPodcast && (
            <Card className="p-6 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-600">
                    <Volume2 className="w-3 h-3 mr-1" />
                    {t('podcastReady')}
                  </Badge>
                </div>
                
                {generatedPodcast.Image && (
                  <img 
                    src={generatedPodcast.Image} 
                    alt={generatedPodcast.Name} 
                    className="w-full h-48 object-cover rounded-lg" 
                  />
                )}
                
                <div>
                  <h4 className="font-semibold text-lg mb-2">{generatedPodcast.Name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {currentDescription}
                  </p>
                </div>
                
                {currentAudioFile && (
                  <div className="space-y-2">
                    <Label>{language === 'FR' ? 'Lecteur audio' : 'Audio player'}</Label>
                    <audio 
                      controls 
                      className="w-full" 
                      src={currentAudioFile} 
                      preload="metadata"
                    >
                      {language === 'FR' ? 'Votre navigateur ne supporte pas l\'élément audio.' : 'Your browser does not support the audio element.'}
                    </audio>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button onClick={handleClose} className="flex-1 btn-adventure">
                    <Play className="w-4 h-4 mr-2" />
                    {t('seeOnMapBtn')}
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PodcastGenerator;
